import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { langSwitchKey } from '../../constants';
import { ChevronDownIcon } from 'lucide-react';

const LangSwitcher = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const containerRef = useRef(null);

  const langImages = useMemo(
    () => ({
      uz: { image: '/langs/uz.svg' },
      ru: { image: '/langs/ru.svg' },
      en: { image: '/langs/en.svg' },
      cn: { image: '/langs/cn.svg' },
    }),
    []
  );

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang.key);
    setOpen(false);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      if (window.innerWidth >= 1024) {
        setOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isDesktop && open) {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDesktop, open]);

  return (
    <div
      ref={containerRef}
      className={`relative pb-3 ${isDesktop ? 'group' : ''}`}
      onClick={() => {
        if (!isDesktop) setOpen(!open);
      }}
    >
      <div className="flex items-center gap-x-2 cursor-pointer select-none">
        <span>
          <ChevronDownIcon
            size={24}
            className={` stroke-white-color duration-300 ${
              (isDesktop && 'group-hover:rotate-180') ||
              (open ? 'rotate-180' : '')
            }`}
          />
        </span>
        <div className="w-10 h-5">
          <img
            loading="lazy"
            src={langImages[language]?.image}
            alt="Selected language"
            className="w-full h-full object-cover rounded-sm"
          />
        </div>
      </div>

      <div
        className={`absolute top-[150%] xl:-left-[60%] lg:-left-[70%] -left-[80%] w-max h-max flex flex-col bg-white-color border border-text-color/30 rounded-md duration-300 ${
          isDesktop
            ? 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-hover:top-[90%]'
            : open
              ? 'opacity-100 pointer-events-auto top-[90%]'
              : 'opacity-0 pointer-events-none top-[150%]'
        }`}
      >
        {langSwitchKey.map((el) => (
          <div
            key={el.id}
            className={`flex items-center py-2 cursor-pointer border-b-2 duration-200 hover:opacity-80 px-4 gap-x-4 text-title-color font-semibold ${
              el.id === 0 ? 'rounded-t-md' : ''
            } ${
              el.id === langSwitchKey.length - 1
                ? 'rounded-b-md border-none'
                : ''
            }`}
            onClick={() => changeLanguage(el)}
          >
            <div className="w-8 h-5">
              <img
                loading="lazy"
                src={el.image}
                alt={el.title}
                className="w-full h-full object-contain rounded-sm"
              />
            </div>
            <span className="text-base">{el.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LangSwitcher;
